import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RacingProvider } from "@/context/RacingContext";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Verify from "@/pages/Verify";
import Admin from "@/pages/Admin";
import Landing from "@/pages/Landing";
import Upgrade from "@/pages/Upgrade";
import Dashboard from "@/pages/Dashboard";
import AddRun from "@/pages/AddRun";
import History from "@/pages/History";
import Analytics from "@/pages/Analytics";
import Profile from "@/pages/Profile";
import PitCrew from "@/pages/PitCrew";
import RunDetail from "@/pages/RunDetail";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { isAuthed } = useAuth();
  if (isAuthed === null)
    return (
      <div className="grid min-h-screen place-items-center bg-mt-bg">
        <Loader2 className="animate-spin text-mt-primary" size={32} />
      </div>
    );
  if (!isAuthed) return <Navigate to="/login" replace />;
  return (
    <RacingProvider>
      <Layout>{children}</Layout>
    </RacingProvider>
  );
}

function LoginRoute() {
  const { isAuthed } = useAuth();
  if (isAuthed === null)
    return (
      <div className="grid min-h-screen place-items-center bg-mt-bg">
        <Loader2 className="animate-spin text-mt-primary" size={32} />
      </div>
    );
  if (isAuthed) return <Navigate to="/" replace />;
  return <Login />;
}

function AdminRoute({ children }) {
  const { isAuthed, user } = useAuth();
  if (isAuthed === null) return <FullSpinner />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return (
    <RacingProvider>
      <Layout>{children}</Layout>
    </RacingProvider>
  );
}

function FullSpinner() {
  return (
    <div className="grid min-h-screen place-items-center bg-mt-bg">
      <Loader2 className="animate-spin text-mt-primary" size={32} />
    </div>
  );
}

function AuthCallback() {
  const { googleSession } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        if (sid) await googleSession(sid);
      } catch (e) {
        // fall through to login on failure
      } finally {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      }
    })();
  }, [googleSession, navigate]);

  return <FullSpinner />;
}

function RootRoute() {
  const { isAuthed } = useAuth();
  if (isAuthed === null) return <FullSpinner />;
  if (!isAuthed) return <Landing />;
  return (
    <RacingProvider>
      <Layout><Dashboard /></Layout>
    </RacingProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/" element={<RootRoute />} />
      <Route path="/upgrade" element={<Protected><Upgrade /></Protected>} />
      <Route path="/add" element={<Protected><AddRun /></Protected>} />
      <Route path="/history" element={<Protected><History /></Protected>} />
      <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
      <Route path="/pit-crew" element={<Protected><PitCrew /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/run/:id" element={<Protected><RunDetail /></Protected>} />
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster
            position="top-center"
            toastOptions={{
              style: { background: "#1A1A1E", color: "#fff", border: "1px solid #1A1A1E" },
              classNames: { title: "font-semibold" },
            }}
          />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
